import { s } from '@/theme';
import { layoutSizes } from '@/theme/layoutSizes';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { r } from '@/theme/tokens/radii';
import type { SxProps, Theme } from '@/ui/primitives';

type ThemeSxObject = Exclude<SxProps<Theme>, readonly unknown[] | ((theme: Theme) => unknown)>;
type ThemeSxFn = (theme: Theme) => ThemeSxObject;

export const sidebarItemMinHeight = layoutSizes.sidebar.itemMinHeight;
export const collapsedSelectedSidebarItemSize = layoutSizes.sidebar.itemMinHeight - 2;
export const sidebarItemFontSize = '13px';
export const sidebarItemFontWeight = 400;
export const selectedSidebarItemFontWeight = 500;
export const sidebarSectionLabelFontSize = '11px';
export const sidebarSectionLabelFontWeight = 500;
export const sidebarSectionLabelBottomSpacing = s('sm');
export const leadingAdornmentSpacing = `calc(${s('sm')} + ${s('md')})`;

export const lightSidebarItemColour = 'rgba(31, 45, 65, 0.9)';
export const darkSidebarItemColour = 'rgba(244, 247, 251, 0.72)';
export const lightSidebarIconColour = 'rgba(31, 45, 65, 0.82)';
export const darkSidebarIconColour = 'rgba(244, 247, 251, 0.68)';
export const lightSidebarSectionLabelColour = 'rgba(31, 45, 65, 0.7)';
export const darkSidebarSectionLabelColour = 'rgba(244, 247, 251, 0.62)';

type BuildSidebarItemRootSxArgs = {
  active: boolean;
  collapsed: boolean;
  disabled: boolean;
  disabledTone?: 'muted' | 'default';
};

type BuildSidebarItemIconSxArgs = {
  active: boolean;
  collapsed: boolean;
  disabled: boolean;
  disabledTone?: 'muted' | 'default';
};

export const buildSidebarItemRootSx =
  ({ active, collapsed, disabled, disabledTone }: BuildSidebarItemRootSxArgs): ThemeSxFn =>
  (theme) => ({
    width: '100%',
    maxWidth: collapsed ? sidebarItemMinHeight : 'none',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    textAlign: 'left',
    overflow: 'hidden',
    position: 'relative',
    paddingX: collapsed ? s('none') : s('lg'),
    paddingY: s('xs'),
    borderRadius: collapsed ? r('pill') : r('sm'),
    textDecoration: 'none',
    color: active
      ? theme.palette.common.white
      : disabled && disabledTone !== 'default'
        ? theme.palette.text.secondary
        : theme.palette.mode === 'dark'
          ? darkSidebarItemColour
          : lightSidebarItemColour,
    fontWeight: active ? selectedSidebarItemFontWeight : sidebarItemFontWeight,
    fontSize: sidebarItemFontSize,
    lineHeight: 1.25,
    minHeight: sidebarItemMinHeight,
    backgroundColor: active && !collapsed ? theme.palette.primary.main : 'transparent',
    cursor: disabled ? 'default' : 'pointer',
    pointerEvents: disabled ? 'none' : 'auto',
    transition: 'background-color 120ms ease, color 120ms ease',
    transitionProperty: 'background-color, color, padding, justify-content',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease',
    '&:hover': active
      ? collapsed
        ? undefined
        : {
            backgroundColor: theme.palette.primary.main,
          }
      : {
          backgroundColor: disabled ? 'transparent' : resolveSemanticColor(theme, 'surfaceHover'),
        },
    '&:focus-visible': {
      outline: `2px solid ${theme.app.state.focus}`,
      outlineOffset: 2,
    },
    '&::before':
      active && collapsed
        ? {
            content: '""',
            position: 'absolute',
            inset: '50% auto auto 50%',
            width: collapsedSelectedSidebarItemSize,
            height: collapsedSelectedSidebarItemSize,
            borderRadius: r('pill'),
            backgroundColor: theme.palette.primary.main,
            transform: 'translate(-50%, -50%)',
          }
        : undefined,
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  });

export const sidebarItemLeadingAdornmentFrameSx = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  mr: leadingAdornmentSpacing,
} as const;

export const buildSidebarItemIconSx =
  ({ active, collapsed, disabled, disabledTone }: BuildSidebarItemIconSxArgs): ThemeSxFn =>
  (theme) => ({
    minWidth: collapsed ? '100% !important' : '20px !important',
    width: collapsed ? '100%' : 20,
    mr: collapsed ? s('none') : s('md'),
    color: active
      ? theme.palette.common.white
      : disabled && disabledTone !== 'default'
        ? theme.palette.text.secondary
        : theme.palette.mode === 'dark'
          ? darkSidebarIconColour
          : lightSidebarIconColour,
    flexShrink: 0,
    opacity: active ? 0.92 : 0.8,
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
      fontSize: `${String(layoutSizes.sidebar.iconSize + 2)}px`,
      stroke: 'currentColor',
      strokeWidth: 0.08,
    },
  });

export const sidebarItemContentSx = {
  minWidth: 0,
  flex: '1 1 auto',
  display: 'flex',
  alignItems: 'center',
  gap: s('md'),
} as const;

export const buildSidebarItemLabelSx = (active: boolean): ThemeSxObject => ({
  minWidth: 0,
  flex: '0 1 auto',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: 'inherit',
  fontWeight: active ? selectedSidebarItemFontWeight : sidebarItemFontWeight,
  opacity: 1,
  transform: 'translateX(0)',
  transition: 'opacity 120ms ease, transform 180ms ease',
});

export const sidebarItemTrailingContentSx = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: s('xs'),
  flexShrink: 0,
} as const;

export const buildSidebarCollapseControlSx =
  (collapsed: boolean): ThemeSxFn =>
  (theme) => ({
    width: '100%',
    maxWidth: collapsed ? sidebarItemMinHeight : 'none',
    boxSizing: 'border-box',
    minHeight: sidebarItemMinHeight,
    border: 'none',
    borderRadius: collapsed ? r('pill') : r('sm'),
    backgroundColor: 'transparent',
    color: theme.palette.mode === 'dark' ? darkSidebarIconColour : lightSidebarIconColour,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    overflow: 'hidden',
    px: collapsed ? s('none') : s('lg'),
    py: s('xs'),
    font: 'inherit',
    '&:hover': {
      backgroundColor: resolveSemanticColor(theme, 'surfaceHover'),
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.app.state.focus}`,
      outlineOffset: 2,
    },
  });

export const buildSidebarCollapseIconSx = (collapsed: boolean): ThemeSxObject => ({
  minWidth: collapsed ? '100% !important' : '20px !important',
  width: collapsed ? '100%' : 20,
  mr: collapsed ? s('none') : s('md'),
  color: 'inherit',
  justifyContent: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: `${String(layoutSizes.sidebar.iconSize + 2)}px`,
    stroke: 'currentColor',
    strokeWidth: 0.08,
  },
});

export const sidebarCollapseLabelSx: ThemeSxFn = (theme) => ({
  color: theme.palette.mode === 'dark' ? darkSidebarItemColour : lightSidebarItemColour,
  fontSize: sidebarItemFontSize,
  fontWeight: sidebarItemFontWeight,
  lineHeight: 1.25,
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const sidebarNavRootSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minWidth: 0,
  overflowX: 'hidden',
} as const;

export const buildSidebarSectionFrameSx = (index: number): ThemeSxObject => ({
  marginTop: index === 0 ? s('none') : s('sm'),
});

export const sidebarSectionLabelSx: ThemeSxFn = (theme) => ({
  color:
    theme.palette.mode === 'dark' ? darkSidebarSectionLabelColour : lightSidebarSectionLabelColour,
  display: 'block',
  paddingBottom: sidebarSectionLabelBottomSpacing,
  paddingX: s('md'),
  fontSize: sidebarSectionLabelFontSize,
  fontWeight: sidebarSectionLabelFontWeight,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  opacity: 1,
  transition: 'opacity 120ms ease',
});

export const buildSidebarItemStackSx = (collapsed: boolean): ThemeSxObject => ({
  alignItems: collapsed ? 'center' : 'stretch',
  marginBottom: s('sm'),
});

export const sidebarFooterTitleSx: ThemeSxFn = (theme) => ({
  color:
    theme.palette.mode === 'dark' ? darkSidebarSectionLabelColour : lightSidebarSectionLabelColour,
  marginTop: s('xl'),
  marginBottom: s('sm'),
  fontSize: sidebarSectionLabelFontSize,
  fontWeight: sidebarSectionLabelFontWeight,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const sidebarFooterFrameSx = {
  marginTop: 'auto',
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  paddingTop: s('2xl'),
  paddingBottom: s('md'),
  backgroundColor: (theme: Theme) => resolveSemanticColor(theme, 'surfacePaper'),
} as const;

export const buildSidebarFooterItemStackSx = ({
  collapsed,
  hasFollowingGroup,
}: {
  collapsed: boolean;
  hasFollowingGroup?: boolean;
}): ThemeSxObject => ({
  alignItems: collapsed ? 'center' : 'stretch',
  marginBottom: hasFollowingGroup === true ? s('xl') : s('sm'),
});

export const sidebarFooterDividerFrameSx = {
  marginBottom: s('sm'),
} as const;

export const sidebarPopoverLeadingAdornmentFrameSx = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
} as const;

export const sidebarPopoverItemTextFrameSx = {
  minWidth: 0,
} as const;

export const sidebarPopoverItemPrimaryTextSx = {
  display: 'block',
  fontSize: sidebarItemFontSize,
  fontWeight: sidebarItemFontWeight,
  lineHeight: 1.2,
} as const;

export const sidebarPopoverItemHelperTextSx = {
  display: 'block',
  mt: s('xxs'),
} as const;

type BuildSidebarPopoverPaperSxArgs = {
  borderColor: string;
  itemFontSize: string;
  surfaceColor: string;
};

type BuildSidebarPopoverMenuItemSxArgs = {
  itemLeadingAdornmentSpacing: string;
  textColor: string;
};

type BuildSidebarPopoverAddActionItemSxArgs = {
  enabled: boolean;
  textColor: string;
  textSecondaryColor: string;
};

export const buildSidebarPopoverPaperSx = ({
  borderColor,
  itemFontSize,
  surfaceColor,
}: BuildSidebarPopoverPaperSxArgs): ThemeSxObject => ({
  marginLeft: s('sm'),
  width: 'fit-content',
  minWidth: 140,
  maxWidth: 'calc(100vw - 24px)',
  border: `1px solid ${borderColor}`,
  borderRadius: `${r('card')} !important`,
  backgroundColor: surfaceColor,
  backgroundImage: 'none',
  boxShadow: 'none',
  '--Paper-overlay': 'none',
  overflow: 'hidden',
  '& .MuiMenuItem-root': {
    fontSize: itemFontSize,
    lineHeight: 1.2,
  },
  '& .MuiMenuItem-root .MuiListItemText-primary': {
    fontSize: itemFontSize,
    lineHeight: 1.2,
  },
});

export const buildSidebarPopoverPaperSxForTheme =
  (itemFontSize: string): ThemeSxFn =>
  (theme) =>
    buildSidebarPopoverPaperSx({
      borderColor: theme.app.colors.border,
      itemFontSize,
      surfaceColor: theme.app.colors.surface,
    });

export const sidebarPopoverTitleFrameSx = {
  px: s('lg'),
  pt: s('md'),
  pb: s('md'),
} as const;

export const buildSidebarPopoverTitleTextSx = (itemFontSize: string): ThemeSxObject => ({
  fontSize: itemFontSize,
  fontWeight: 600,
});

export const sidebarPopoverMenuListSx = {
  pt: s('none'),
  pb: s('none'),
} as const;

export const buildSidebarPopoverMenuItemSx = ({
  itemLeadingAdornmentSpacing,
  textColor,
}: BuildSidebarPopoverMenuItemSxArgs): ThemeSxObject => ({
  px: s('lg'),
  py: s('md'),
  borderRadius: r('none'),
  typography: 'body',
  lineHeight: 1.2,
  color: textColor,
  gap: 0,
  '& [data-sidebar-popover-leading-adornment="true"]': {
    marginRight: itemLeadingAdornmentSpacing,
  },
});

export const buildSidebarPopoverAddActionItemSx = ({
  enabled,
  textColor,
  textSecondaryColor,
}: BuildSidebarPopoverAddActionItemSxArgs): ThemeSxObject => ({
  px: s('lg'),
  py: s('md'),
  borderRadius: r('none'),
  display: 'flex',
  alignItems: 'center',
  gap: s('sm'),
  color: enabled ? textColor : textSecondaryColor,
  cursor: enabled ? 'pointer' : 'default',
  opacity: enabled ? 1 : 0.7,
  '& .MuiListItemIcon-root': {
    minWidth: '20px !important',
    width: 20,
    mr: 0,
    color: 'inherit',
    flexShrink: 0,
  },
  '& .MuiListItemText-root': { m: 0 },
  '& .MuiListItemText-primary': { lineHeight: 1.2 },
});
